import type { ReactNode } from "react";

// Primary route definitions with metadata
export const ROUTES = {
  HOME: {
    path: "/" as const,
    name: "Home",
    title: "LICO - Learn and Innovate Technologies Limited",
    description:
      "Professional business training, entrepreneurship development, sustainable energy solutions, and real estate services across Nigeria. Transform your future with LICO.",
    layoutType: "main",
  },
  ABOUT: {
    path: "/about" as const,
    name: "About Us",
    title: "About LICO - Learn and Innovate Technologies Limited",
    description:
      "Learn about LICO's mission to transform lives across Nigeria through business training, entrepreneurship development, solar energy solutions, and real estate services.",
    layoutType: "main",
  },
  BLOGS: {
    path: "/blog" as const,
    name: "Blog",
    title: "LICO Blog - Technologies & Business Insights for Nigeria",
    description:
      "Stay updated with expert insights on business training, tech skills, entrepreneurship, solar energy, and real estate across Nigeria.",
    layoutType: "main",
  },
  CONTACTUS: {
    path: "/contact-us" as const,
    name: "Contact Us",
    title: "Contact LICO Technologies",
    description:
      "Get in touch with LICO for professional training, solar energy solutions, healthcare services, and real estate services in Nigeria.",
    layoutType: "main",
  },
  SERVICES: {
    path: "/services" as const,
    name: "Services",
    title: "Our Services - LICO Technologies",
    description:
      "Comprehensive solutions for career, energy, health, and wealth - integrated into one seamless ecosystem.",
    layoutType: "main",
  },

  // Individual Service Pages
  TRAINING_SERVICE: {
    path: "/services/training" as const,
    name: "Training & Entrepreneurship",
    title: "Training & Entrepreneurship - LICO",
    description:
      "Expert training in business, technology, and entrepreneurship to build the career or business you deserve.",
    layoutType: "main" as const,
  },

  SOLAR_SERVICE: {
    path: "/services/solar" as const,
    name: "Solar Energy Solutions",
    title: "Solar Energy Solutions - LICO",
    description:
      "Clean, reliable solar power that guarantees you never lack energy for your upgraded lifestyle.",
    layoutType: "main" as const,
  },

  HEALTHCARE_SERVICE: {
    path: "/services/healthcare" as const,
    name: "Remote Healthcare",
    title: "Remote Healthcare Services - LICO",
    description:
      "Top-tier medical care from anywhere to restore balance when illness or fatigue comes knocking.",
    layoutType: "main" as const,
  },

  REAL_ESTATE_SERVICE: {
    path: "/services/real-estate" as const,
    name: "Smart Real Estate",
    title: "Smart Real Estate Solutions - LICO",
    description:
      "Build wealth through intelligent property solutions - from smart homes to investment advisory.",
    layoutType: "main" as const,
  },

  TRAINING: {
    path: "/training" as const,
    name: "Training",
    title: "Professional Training - LICO Technologies",
    description:
      "Discover LICO's professional training programs in business, technology, and entrepreneurship designed to empower individuals and teams.",
    layoutType: "main",
  },
  PRODUCTS: {
    path: "/products" as const,
    name: "Products",
    title: "Our Products - LICO Technologies",
    description:
      "Browse LICO's range of products including tech solutions, solar energy systems, and innovative tools for businesses.",
    layoutType: "main",
  },

  PRIVACY: {
    path: "/privacy" as const,
    name: "Privacy Policy",
    title: "Privacy Policy - LICO Technologies",
    description:
      "How we protect your personal information with industry-standard security measures.",
    layoutType: "main",
  },
  TERMS: {
    path: "/terms" as const,
    name: "Terms & Conditions",
    title: "Terms and Conditions - LICO",
    description: "Terms and conditions for using LICO services and platform.",
    layoutType: "main",
  },
  FAQ: {
    path: "/faq" as const,
    name: "FAQ",
    title: "Frequently Asked Questions - LICO",
    description:
      "Get answers to common questions about LICO services, training programs, and solutions.",
    layoutType: "main",
  },
  BLOG_POST: {
    path: "/blog/:slug" as const,
    name: "Blog Post",
    title: "Blog Article | LICO Insights",
    description:
      "Read expert insights on business, technology, entrepreneurship, and innovation from LICO professionals.",
    layoutType: "main",
  },
  BLOG_CATEGORY: {
    path: "/blog/category/:category" as const,
    name: "Blog Category",
    title: "Blog Articles by Category | LICO Blog",
    description:
      "Browse blog articles by category including business, technology, entrepreneurship, and innovation insights.",
    layoutType: "main",
  },
  BLOG_AUTHOR: {
    path: "/blog/author/:author" as const,
    name: "Blog Author",
    title: "Articles by Author | LICO Experts",
    description:
      "Read articles from our expert LICO professionals and industry specialists.",
    layoutType: "main",
  },

  // ==================== ADMIN ROUTES ====================
  ADMIN: {
    path: "/admin" as const,
    name: "Admin",
    title: "Admin Panel - LICO",
    description:
      "Administrative panel for managing LICO platform content and users.",
    layoutType: "admin",
  },
  ADMIN_LOGIN: {
    path: "/admin/login" as const,
    name: "Admin Login",
    title: "Admin Login - LICO",
    description: "Login to LICO admin panel",
    layoutType: "none",
  },
  ADMIN_DASHBOARD: {
    path: "/admin/dashboard" as const,
    name: "Dashboard",
    title: "Dashboard - LICO Admin",
    description: "Admin dashboard overview",
    layoutType: "admin",
  },
  ADMIN_APPS: {
    path: "/admin/apps" as const,
    name: "Apps Management",
    title: "Apps Management - LICO Admin",
    description: "Manage applications and products",
    layoutType: "admin",
  },
  ADMIN_BLOGS: {
    path: "/admin/blog" as const,
    name: "Blogs Management",
    title: "Blogs Management - LICO Admin",
    description: "Manage blog posts and content",
    layoutType: "admin",
  },
  ADMIN_CONTACTS: {
    path: "/admin/contacts" as const,
    name: "Contacts Management",
    title: "Contacts Management - LICO Admin",
    description: "Manage contact inquiries",
    layoutType: "admin",
  },
  ADMIN_CONTACT_DETAIL: {
    path: "/admin/contacts/:id" as const,
    name: "Contact Detail",
    title: "Contact Detail - LICO Admin",
    description: "View contact inquiry details",
    layoutType: "admin",
  },
  ADMIN_ADMINS: {
    path: "/admin/admins" as const,
    name: "Admins Management",
    title: "Admins Management - LICO Admin",
    description: "Manage admin users",
    layoutType: "admin",
  },
  ADMIN_FORGOT_PASSWORD: {
    path: "/admin/forgot-password" as const,
    name: "Forgot Password",
    title: "Forgot Password - LICO Admin",
    description: "Reset your admin password",
    layoutType: "none",
  },
  ADMIN_RESET_PASSWORD: {
    path: "/admin/reset-password" as const,
    name: "Reset Password",
    title: "Reset Password - LICO Admin",
    description: "Set a new password for your admin account",
    layoutType: "none",
  },

  SEO_TEST: {
    path: "/seo-test" as const,
    layoutType: "none",
  },

  // Homepage sections (for smooth scrolling)
  SECTIONS: {
    HOME_HERO: "#home" as const,
    ABOUT_US: "#about" as const,
    SERVICES: "#services" as const,
    VALUES: "#values" as const,
    CONTACT_CTA: "#contact" as const,
  },

  // External links
  EXTERNAL: {
    // Social media
    FACEBOOK: "https://facebook.com/licotech",
    INSTAGRAM: "https://instagram.com/licotech",
    TWITTER: "https://twitter.com/licotech",
    LINKEDIN: "https://linkedin.com/company/licotech",

    // Contact
    PHONE: "tel:+2349078801125",
    EMAIL: "mailto:info@learnandinnovatespace.com",
    EMAIL_DISPLAY: "info@learnandinnovatespace.com",
    PHONE_DISPLAY: "+234 907 880 1125",
  },

  // Essential company info
  COMPANY: {
    NAME: "LICO",
    FULL_NAME: "Learn and Innovate Technologies Limited",
    TAGLINE: "Transforming Nigeria Through Innovation",
    DESCRIPTION:
      "We integrate sustainable power, smart real estate, world-class training, and instant healthcare into one seamless ecosystem.",
    LOCATION: "No. 75 Chime Avenue, New Haven, Enugu State, Nigeria",
  },
} as const;

// Type for valid route paths
export type RoutePath =
  | typeof ROUTES.HOME.path
  | typeof ROUTES.ABOUT.path
  | typeof ROUTES.BLOGS.path
  | typeof ROUTES.CONTACTUS.path
  | typeof ROUTES.SERVICES.path
  | typeof ROUTES.TRAINING_SERVICE.path
  | typeof ROUTES.SOLAR_SERVICE.path
  | typeof ROUTES.HEALTHCARE_SERVICE.path
  | typeof ROUTES.REAL_ESTATE_SERVICE.path
  | typeof ROUTES.TRAINING.path
  | typeof ROUTES.PRODUCTS.path
  | typeof ROUTES.TERMS.path
  | typeof ROUTES.PRIVACY.path
  | typeof ROUTES.FAQ.path
  | typeof ROUTES.BLOG_POST.path
  | typeof ROUTES.BLOG_CATEGORY.path
  | typeof ROUTES.BLOG_AUTHOR.path
  | typeof ROUTES.ADMIN.path
  | typeof ROUTES.ADMIN_LOGIN.path
  | typeof ROUTES.ADMIN_DASHBOARD.path
  | typeof ROUTES.ADMIN_APPS.path
  | typeof ROUTES.ADMIN_BLOGS.path
  | typeof ROUTES.ADMIN_CONTACTS.path
  | typeof ROUTES.ADMIN_CONTACT_DETAIL.path
  | typeof ROUTES.ADMIN_ADMINS.path
  | typeof ROUTES.ADMIN_FORGOT_PASSWORD.path
  | typeof ROUTES.ADMIN_RESET_PASSWORD.path;

// Navigation menu structure
export const NAVIGATION_MENU = [
  {
    label: "Home",
    route: ROUTES.HOME,
    icon: "home",
    type: "link" as const,
  },
  {
    label: "About",
    route: ROUTES.ABOUT,
    icon: "info",
    type: "link" as const,
  },
  {
    label: "Services",
    route: ROUTES.SERVICES,
    icon: "business",
    type: "link" as const,
  },
  {
    label: "Blog",
    route: ROUTES.BLOGS,
    icon: "article",
    type: "link" as const,
  },
  {
    label: "Contact",
    route: ROUTES.CONTACTUS,
    icon: "contact",
    type: "link" as const,
  },
  {
    label: "Training",
    route: ROUTES.TRAINING,
    icon: "school",
    type: "link" as const,
  },
  {
    label: "Products",
    route: ROUTES.PRODUCTS,
    icon: "inventory",
    type: "link" as const,
  },
  {
    label: "Terms & Conditions",
    route: ROUTES.TERMS,
    icon: "gavel",
    type: "link" as const,
  },
  {
    label: "Privacy Policy",
    route: ROUTES.PRIVACY,
    icon: "lock",
    type: "link" as const,
  },
  {
    label: "FAQ",
    route: ROUTES.FAQ,
    icon: "help",
    type: "link" as const,
  },
] as const;

// Footer navigation structure
export const FOOTER_NAVIGATION = {
  Company: [
    {
      label: "About Us",
      route: ROUTES.ABOUT,
      type: "link" as const,
    },
    {
      label: "Our Services",
      route: ROUTES.SERVICES,
      type: "link" as const,
    },
    {
      label: "Contact Email",
      route: ROUTES.EXTERNAL.EMAIL,
      type: "link" as const,
    },
  ],
  Services: [
    {
      label: "Training & Entrepreneurship",
      route: ROUTES.TRAINING_SERVICE,
      type: "link" as const,
    },
    {
      label: "Solar Energy",
      route: ROUTES.SOLAR_SERVICE,
      type: "link" as const,
    },
    {
      label: "Healthcare",
      route: ROUTES.HEALTHCARE_SERVICE,
      type: "link" as const,
    },
    {
      label: "Real Estate",
      route: ROUTES.REAL_ESTATE_SERVICE,
      type: "link" as const,
    },
  ],
  Support: [
    {
      label: "Blog",
      route: ROUTES.BLOGS,
      type: "link" as const,
    },
    {
      label: "Contact Us",
      route: ROUTES.CONTACTUS,
      type: "link" as const,
    },
    {
      label: "Phone",
      route: ROUTES.EXTERNAL.PHONE,
      type: "link" as const,
    },
  ],
} as const;

// Page metadata for SEO
export const PAGE_METADATA: Record<
  RoutePath,
  {
    title: string;
    description: string;
    keywords: string;
    ogImage: string;
  }
> = {
  "/": {
    title: ROUTES.HOME.title,
    description: ROUTES.HOME.description,
    keywords:
      "LICO, business training Nigeria, tech training, entrepreneurship development, solar energy Nigeria, solar energy Enugu, real estate Nigeria, Learn and Innovate, professional training, sustainable energy, healthcare services",
    ogImage: "/lico-og-image.png",
  },
  "/about": {
    title: ROUTES.ABOUT.title,
    description: ROUTES.ABOUT.description,
    keywords:
      "about LICO, Learn and Innovate Technologies Limited, business training company Nigeria, solar energy company Enugu, healthcare services Nigeria, real estate services",
    ogImage: "/lico-about-og.png",
  },
  "/blog": {
    title: ROUTES.BLOGS.title,
    description: ROUTES.BLOGS.description,
    keywords:
      "LICO blog, business insights Nigeria, technology tips, entrepreneurship advice, solar energy information, real estate Nigeria",
    ogImage: "/lico-blog-og.png",
  },
  "/contact-us": {
    title: ROUTES.CONTACTUS.title,
    description: ROUTES.CONTACTUS.description,
    keywords:
      "contact LICO, LICO Nigeria contact, business training inquiry, solar energy consultation, healthcare services",
    ogImage: "/lico-contact-og.png",
  },
  "/services": {
    title: ROUTES.SERVICES.title,
    description: ROUTES.SERVICES.description,
    keywords:
      "LICO services, training services, entrepreneurship development, solar energy solutions, healthcare services, real estate services Nigeria",
    ogImage: "/lico-services-og.png",
  },
  "/services/training": {
    title: ROUTES.TRAINING_SERVICE.title,
    description: ROUTES.TRAINING_SERVICE.description,
    keywords:
      "business training, tech training, entrepreneurship programs, professional development, startup support Nigeria",
    ogImage: "/lico-training-og.png",
  },
  "/services/solar": {
    title: ROUTES.SOLAR_SERVICE.title,
    description: ROUTES.SOLAR_SERVICE.description,
    keywords:
      "solar energy Nigeria, solar installation, renewable energy, clean power, solar panels, energy solutions",
    ogImage: "/lico-solar-og.png",
  },
  "/services/healthcare": {
    title: ROUTES.HEALTHCARE_SERVICE.title,
    description: ROUTES.HEALTHCARE_SERVICE.description,
    keywords:
      "telemedicine Nigeria, remote healthcare, health monitoring, online consultations, healthcare technology",
    ogImage: "/lico-healthcare-og.png",
  },
  "/services/real-estate": {
    title: ROUTES.REAL_ESTATE_SERVICE.title,
    description: ROUTES.REAL_ESTATE_SERVICE.description,
    keywords:
      "real estate Nigeria, smart homes, property investment, construction services, property development",
    ogImage: "/lico-real-estate-og.png",
  },
  "/training": {
    title: ROUTES.TRAINING.title,
    description: ROUTES.TRAINING.description,
    keywords:
      "LICO training, business training Nigeria, technical training programs, professional development, entrepreneurship training, skills development",
    ogImage: "/lico-training-og.png",
  },
  "/products": {
    title: ROUTES.PRODUCTS.title,
    description: ROUTES.PRODUCTS.description,
    keywords:
      "LICO products, tech products Nigeria, business solutions, solar energy systems, innovative products, solar panels Nigeria",
    ogImage: "/lico-products-og.png",
  },
  "/terms": {
    title: ROUTES.TERMS.title,
    description: ROUTES.TERMS.description,
    keywords: "LICO terms, terms of service, user agreement, platform terms",
    ogImage: "/lico-terms-og.png",
  },
  "/privacy": {
    title: ROUTES.PRIVACY.title,
    description: ROUTES.PRIVACY.description,
    keywords: "LICO privacy, privacy policy, data protection, user privacy",
    ogImage: "/lico-privacy-og.png",
  },
  "/faq": {
    title: ROUTES.FAQ.title,
    description: ROUTES.FAQ.description,
    keywords: "LICO FAQ, frequently asked questions, help center, support",
    ogImage: "/lico-faq-og.png",
  },
  "/blog/:slug": {
    title: ROUTES.BLOG_POST.title,
    description: ROUTES.BLOG_POST.description,
    keywords:
      "LICO blog post, article, insights, business, technology, innovation",
    ogImage: "/lico-blog-post-og.png",
  },
  "/blog/category/:category": {
    title: ROUTES.BLOG_CATEGORY.title,
    description: ROUTES.BLOG_CATEGORY.description,
    keywords: "LICO blog category, articles, business, technology",
    ogImage: "/lico-blog-og.png",
  },
  "/blog/author/:author": {
    title: ROUTES.BLOG_AUTHOR.title,
    description: ROUTES.BLOG_AUTHOR.description,
    keywords: "LICO author, expert, professional, articles",
    ogImage: "/lico-blog-og.png",
  },
  "/admin": {
    title: ROUTES.ADMIN.title,
    description: ROUTES.ADMIN.description,
    keywords: "admin panel, content management, user management, LICO admin",
    ogImage: "/lico-admin-og.png",
  },
  "/admin/login": {
    title: ROUTES.ADMIN_LOGIN.title,
    description: ROUTES.ADMIN_LOGIN.description,
    keywords: "admin login, authentication, admin access",
    ogImage: "/lico-admin-og.png",
  },
  "/admin/dashboard": {
    title: ROUTES.ADMIN_DASHBOARD.title,
    description: ROUTES.ADMIN_DASHBOARD.description,
    keywords: "admin dashboard, overview, statistics",
    ogImage: "/lico-admin-og.png",
  },
  "/admin/apps": {
    title: ROUTES.ADMIN_APPS.title,
    description: ROUTES.ADMIN_APPS.description,
    keywords: "apps management, products management, admin",
    ogImage: "/lico-admin-og.png",
  },
  "/admin/blog": {
    title: ROUTES.ADMIN_BLOGS.title,
    description: ROUTES.ADMIN_BLOGS.description,
    keywords: "blog management, content management, posts",
    ogImage: "/lico-admin-og.png",
  },
  "/admin/contacts": {
    title: ROUTES.ADMIN_CONTACTS.title,
    description: ROUTES.ADMIN_CONTACTS.description,
    keywords: "contact management, inquiries, messages",
    ogImage: "/lico-admin-og.png",
  },
  "/admin/contacts/:id": {
    title: ROUTES.ADMIN_CONTACT_DETAIL.title,
    description: ROUTES.ADMIN_CONTACT_DETAIL.description,
    keywords: "contact detail, inquiry detail, message detail",
    ogImage: "/lico-admin-og.png",
  },
  "/admin/admins": {
    title: ROUTES.ADMIN_ADMINS.title,
    description: ROUTES.ADMIN_ADMINS.description,
    keywords: "admin management, users, permissions",
    ogImage: "/lico-admin-og.png",
  },
  "/admin/forgot-password": {
    title: ROUTES.ADMIN_FORGOT_PASSWORD.title,
    description: ROUTES.ADMIN_FORGOT_PASSWORD.description,
    keywords: "forgot password, reset password, admin recovery",
    ogImage: "/lico-admin-og.png",
  },
  "/admin/reset-password": {
    title: ROUTES.ADMIN_RESET_PASSWORD.title,
    description: ROUTES.ADMIN_RESET_PASSWORD.description,
    keywords: "reset password, new password, admin password",
    ogImage: "/lico-admin-og.png",
  },
};

// Route type definition
type Route = {
  path: string;
  name?: string;
  title?: string;
  description?: string;
  element?: ReactNode;
  [key: string]: any;
};

// Helper functions
export const getRouteByPath = (path: string): Route | undefined => {
  const allRoutes: Route[] = [
    ROUTES.HOME,
    ROUTES.ABOUT,
    ROUTES.BLOGS,
    ROUTES.CONTACTUS,
    ROUTES.SERVICES,
    ROUTES.TRAINING_SERVICE,
    ROUTES.SOLAR_SERVICE,
    ROUTES.HEALTHCARE_SERVICE,
    ROUTES.REAL_ESTATE_SERVICE,
    ROUTES.TRAINING,
    ROUTES.PRODUCTS,
    ROUTES.TERMS,
    ROUTES.PRIVACY,
    ROUTES.FAQ,
    ROUTES.BLOG_POST,
    ROUTES.BLOG_CATEGORY,
    ROUTES.BLOG_AUTHOR,
    ROUTES.ADMIN,
    ROUTES.ADMIN_LOGIN,
    ROUTES.ADMIN_DASHBOARD,
    ROUTES.ADMIN_APPS,
    ROUTES.ADMIN_BLOGS,
    ROUTES.ADMIN_CONTACTS,
    ROUTES.ADMIN_CONTACT_DETAIL,
    ROUTES.ADMIN_ADMINS,
    ROUTES.ADMIN_FORGOT_PASSWORD,
    ROUTES.ADMIN_RESET_PASSWORD,
  ];

  return allRoutes.find((route) => route.path === path);
};

export const isExternalUrl = (url: string): boolean => {
  return (
    url.startsWith("http") ||
    url.startsWith("tel:") ||
    url.startsWith("mailto:") ||
    url.startsWith("#")
  );
};

export const getPageMetadata = (path: string) => {
  return PAGE_METADATA[path as RoutePath] || PAGE_METADATA[ROUTES.HOME.path];
};

export const isRouteObject = (
  route: any,
): route is {
  name: string;
  path: string;
} => {
  return typeof route === "object" && "path" in route;
};

export const getRoutePath = (route: any): string => {
  return isRouteObject(route) ? route.path : route;
};

export const getRouteName = (route: any): string => {
  return isRouteObject(route) ? route.name : route;
};

// Route validation
export const isValidRoute = (path: string): boolean => {
  const allValidPaths: RoutePath[] = [
    ROUTES.HOME.path,
    ROUTES.ABOUT.path,
    ROUTES.BLOGS.path,
    ROUTES.CONTACTUS.path,
    ROUTES.SERVICES.path,
    ROUTES.TRAINING_SERVICE.path,
    ROUTES.SOLAR_SERVICE.path,
    ROUTES.HEALTHCARE_SERVICE.path,
    ROUTES.REAL_ESTATE_SERVICE.path,
    ROUTES.TRAINING.path,
    ROUTES.PRODUCTS.path,
    ROUTES.TERMS.path,
    ROUTES.PRIVACY.path,
    ROUTES.FAQ.path,
    ROUTES.BLOG_POST.path,
    ROUTES.BLOG_CATEGORY.path,
    ROUTES.BLOG_AUTHOR.path,
    ROUTES.ADMIN.path,
    ROUTES.ADMIN_LOGIN.path,
    ROUTES.ADMIN_DASHBOARD.path,
    ROUTES.ADMIN_APPS.path,
    ROUTES.ADMIN_BLOGS.path,
    ROUTES.ADMIN_CONTACTS.path,
    ROUTES.ADMIN_CONTACT_DETAIL.path,
    ROUTES.ADMIN_ADMINS.path,
    ROUTES.ADMIN_FORGOT_PASSWORD.path,
    ROUTES.ADMIN_RESET_PASSWORD.path,
  ];

  return allValidPaths.includes(path as RoutePath);
};

// Social media sharing
export const getSocialShareUrl = (
  platform: string,
  url: string,
  title: string,
): string | boolean => {
  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);

  const shareUrls: Record<string, string> = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    twitter: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
    linkedin: `https://www.linkedin.com/shareArticle?mini=true&url=${encodedUrl}&title=${encodedTitle}`,
    instagram: ROUTES.EXTERNAL.INSTAGRAM,
  };

  return shareUrls[platform] || false;
};

// Check if route is active
export const isActiveRoute = (
  currentPath: string,
  routePath: string,
): boolean => {
  if (routePath === ROUTES.HOME.path) {
    return currentPath === routePath;
  }
  return currentPath.startsWith(routePath);
};

// Navigation utilities for smooth scrolling
export const scrollToSection = (
  sectionId: string,
  offset: number = 120,
): void => {
  const element = document.querySelector(sectionId);
  if (element) {
    const elementPosition =
      element.getBoundingClientRect().top + window.pageYOffset;
    const offsetPosition = elementPosition - offset;

    window.scrollTo({
      top: offsetPosition,
      behavior: "smooth",
    });
  }
};

// Breadcrumb generation
export const generateBreadcrumbs = (
  pathname: string,
): { name: string; path: RoutePath }[] => {
  const breadcrumbs: { name: string; path: RoutePath }[] = [
    {
      name: "Home",
      path: ROUTES.HOME.path,
    },
  ];

  if (pathname === ROUTES.ABOUT.path) {
    breadcrumbs.push({
      name: "About Us",
      path: ROUTES.ABOUT.path,
    });
  } else if (pathname === ROUTES.BLOGS.path) {
    breadcrumbs.push({
      name: "Blog",
      path: ROUTES.BLOGS.path,
    });
  } else if (pathname === ROUTES.CONTACTUS.path) {
    breadcrumbs.push({
      name: "Contact Us",
      path: ROUTES.CONTACTUS.path,
    });
  } else if (pathname === ROUTES.SERVICES.path) {
    breadcrumbs.push({
      name: "Services",
      path: ROUTES.SERVICES.path,
    });
  } else if (pathname === ROUTES.TRAINING_SERVICE.path) {
    breadcrumbs.push({
      name: "Services",
      path: ROUTES.SERVICES.path,
    });
    breadcrumbs.push({
      name: "Training & Entrepreneurship",
      path: ROUTES.TRAINING_SERVICE.path,
    });
  } else if (pathname === ROUTES.SOLAR_SERVICE.path) {
    breadcrumbs.push({
      name: "Services",
      path: ROUTES.SERVICES.path,
    });
    breadcrumbs.push({
      name: "Solar Energy",
      path: ROUTES.SOLAR_SERVICE.path,
    });
  } else if (pathname === ROUTES.HEALTHCARE_SERVICE.path) {
    breadcrumbs.push({
      name: "Services",
      path: ROUTES.SERVICES.path,
    });
    breadcrumbs.push({
      name: "Healthcare",
      path: ROUTES.HEALTHCARE_SERVICE.path,
    });
  } else if (pathname === ROUTES.REAL_ESTATE_SERVICE.path) {
    breadcrumbs.push({
      name: "Services",
      path: ROUTES.SERVICES.path,
    });
    breadcrumbs.push({
      name: "Real Estate",
      path: ROUTES.REAL_ESTATE_SERVICE.path,
    });
  } else if (pathname === ROUTES.TRAINING.path) {
    breadcrumbs.push({
      name: "Training",
      path: ROUTES.TRAINING.path,
    });
  } else if (pathname === ROUTES.PRODUCTS.path) {
    breadcrumbs.push({
      name: "Products",
      path: ROUTES.PRODUCTS.path,
    });
  } else if (pathname === ROUTES.TERMS.path) {
    breadcrumbs.push({
      name: "Terms & Conditions",
      path: ROUTES.TERMS.path,
    });
  } else if (pathname === ROUTES.PRIVACY.path) {
    breadcrumbs.push({
      name: "Privacy Policy",
      path: ROUTES.PRIVACY.path,
    });
  } else if (pathname === ROUTES.FAQ.path) {
    breadcrumbs.push({
      name: "FAQ",
      path: ROUTES.FAQ.path,
    });
  } else if (pathname.startsWith("/admin")) {
    breadcrumbs.push({
      name: "Admin",
      path: ROUTES.ADMIN.path,
    });
  }

  return breadcrumbs;
};

// SEO and Open Graph utilities
export const generateStructuredData = (route: Route): object => {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: route.title,
    description: route.description,
    url: `https://learnandinnovatespace.com${route.path}`,
    publisher: {
      "@type": "Organization",
      name: ROUTES.COMPANY.NAME,
      url: "https://learnandinnovatespace.com",
      logo: "https://learnandinnovatespace.com/lico-technologies-logo.png",
    },
  };
};

// Text snippets for consistent messaging
export const TEXT_SNIPPETS = {
  COPYRIGHT: `© ${new Date().getFullYear()} ${
    ROUTES.COMPANY.FULL_NAME
  }. All rights reserved.`,
  MADE_WITH_LOVE: `Made with ❤️ in Nigeria for Africa`,
  PLATFORM_NOTE: ROUTES.COMPANY.DESCRIPTION,
  TAGLINE: ROUTES.COMPANY.TAGLINE,
} as const;

// Service categories for organization
export const SERVICES = {
  TRAINING: {
    name: "Training & Entrepreneurship",
    icon: "school",
    description:
      "Expert training in business, technology, and entrepreneurship",
    path: ROUTES.TRAINING_SERVICE.path,
  },
  SOLAR: {
    name: "Solar Energy Solutions",
    icon: "wb_sunny",
    description: "Clean, reliable solar power for your lifestyle",
    path: ROUTES.SOLAR_SERVICE.path,
  },
  HEALTHCARE: {
    name: "Remote Healthcare",
    icon: "local_hospital",
    description: "Top-tier medical care through telemedicine",
    path: ROUTES.HEALTHCARE_SERVICE.path,
  },
  REAL_ESTATE: {
    name: "Smart Real Estate",
    icon: "home",
    description: "Build wealth through intelligent property solutions",
    path: ROUTES.REAL_ESTATE_SERVICE.path,
  },
} as const;
